import React, { useState } from "react";
import { ArrowRight, Check, ExternalLink, Mail, Shield, Sparkles, MoveDown } from "lucide-react";
import BookSessionModal from "../components/BookSessionModal";

const MGRANT_URL = "/sites/mgrant/";
const MGRANT_NUO_URL = "/sites/mgrant-nuo/";
const EMAIL = "partnerships@dhwaniris.com";
const MGRANT_LOGO = "/assets/mgrant-logo.png";
const DHWANI_LOGO = "/assets/dhwani-logo.png";

const donorStats = [
  { num: "40+", lbl: "Donors" },
  { num: "₹5,000 Cr+", lbl: "Grants governed" },
  { num: "1,500+", lbl: "NGO grantees" },
];
const ngoStats = [
  { num: "10+", lbl: "Non-Profits" },
  { num: "200+", lbl: "Projects" },
  { num: "₹500 Cr+", lbl: "Portfolio" },
];

const donorFeats = [
  "End-to-end CSR grant lifecycle — discovery to closure",
  "3,000+ pre-vetted NGOs with 12A/80G, FCRA checks",
  "ERP-integrated fund flow (SAP · Tally · Oracle)",
  "CSR-2, AAP, Asset Geo-tagging & MCA audit trail built-in",
];
const ngoFeats = [
  "Fundraising CRM with AI-powered RFP scouting",
  "LFA impact measurement — Activities → Outcomes → Impact",
  "FCRA, FC-4, 80G receipts & compliance calendar",
  "Beneficiary registry + Tally / VOLAC / SAP integrations",
];

export default function Landing() {
  const [modal, setModal] = useState({ open: false, source: "landing", accent: "maroon" });
  const openBook = (source, accent = "maroon") => setModal({ open: true, source, accent });

  return (
    <>
      {/* ———— TOP NAV ———— */}
      <header className="topbar" data-testid="site-topbar">
        <div className="topbar-inner">
          <a href="/" className="brand" data-testid="brand-home-link" aria-label="mGrant home">
            <img src={MGRANT_LOGO} alt="mGrant" className="brand-logo-img" />
          </a>
          <nav className="nav-actions" aria-label="Primary">
            <a className="nav-link" href="#choose" data-testid="nav-choose-link">Choose product</a>
            <a className="nav-link" href="#about" data-testid="nav-about-link">About</a>
            <a
              className="nav-link"
              href="https://x.mgrant.in"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-insights-link"
            >
              Insights
            </a>
            <button className="nav-link" onClick={() => openBook("landing", "maroon")} style={{ background: "transparent", border: 0, cursor: "pointer" }} data-testid="nav-contact-link">Contact</button>
          </nav>
        </div>
      </header>

      {/* ———— HERO ———— */}
      <section className="hero" data-testid="hero-section">
        <div className="hero-inner">
          <span className="eyebrow anim" data-testid="hero-eyebrow">
            <span className="eyebrow-dot" /> Welcome to mgrant.in
          </span>
          <h1 className="anim d1" data-testid="hero-heading">
            Grant management, built for <em>Indian</em> CSR <em className="or">—&nbsp;and&nbsp;—</em> <em>Indian</em> NGOs.
          </h1>
          <p className="hero-sub anim d2" data-testid="hero-subtitle">
            Two focused products under one name. Pick the one built for how you work —
            <strong> mGrant</strong> for donors and funders, or <strong>mGrant NUO</strong> for non-profit organisations.
          </p>
          <div className="hero-meta anim d3" data-testid="hero-meta">
            <span>40+ donor orgs</span><span className="bullet" />
            <span>1,500+ NGO grantees</span><span className="bullet" />
            <span>₹5,500 Cr+ governed</span>
          </div>
          <a href="#choose" className="scroll-hint anim d4" data-testid="hero-scroll-hint">
            <span className="scroll-hint-line" />
            Choose your experience
            <MoveDown size={14} />
          </a>
        </div>
      </section>

      {/* ———— PRODUCT SELECTOR ———— */}
      <section id="choose" className="select-section" data-testid="select-section">
        <div className="select-wrap">
          <div className="select-head">
            <h2 data-testid="select-heading">
              Two products. One platform family.
              <br />
              <span className="font-serif-accent" style={{ color: "#8B1A1A" }}>Which one fits you?</span>
            </h2>
            <p data-testid="select-subtext">
              Each product has its own workflows, compliance rails and dedicated
              website. Pick yours and continue.
            </p>
          </div>

          <div className="cards">
            {/* ———— DONOR CARD ———— */}
            <article className="product-card card-donors" data-testid="product-card-donors">
              <div className="card-head">
                <img src={MGRANT_LOGO} alt="mGrant" className="card-logo-img" />
                <span className="card-badge" data-testid="donors-card-badge">
                  <Shield size={12} /> For Donors & Funders
                </span>
              </div>

              <h3 className="card-title sr-only" data-testid="donors-card-title">mGrant</h3>
              <div className="card-tagline" data-testid="donors-card-tagline">
                The grant-management platform for Indian CSR.
              </div>
              <p className="card-desc" data-testid="donors-card-desc">
                Built for corporate CSR teams, foundations and grant-makers. Find partners,
                conduct due diligence, sanction grants, disburse funds, track impact — and meet
                every MCA compliance obligation.
              </p>

              <div className="card-stats" data-testid="donors-card-stats">
                {donorStats.map((s, i) => (
                  <div key={i}>
                    <div className="stat-num">{s.num}</div>
                    <div className="stat-lbl">{s.lbl}</div>
                  </div>
                ))}
              </div>

              <ul className="card-feats" data-testid="donors-card-feats">
                {donorFeats.map((f, i) => (
                  <li className="card-feat" key={i}>
                    <span className="feat-tick"><Check size={11} strokeWidth={3} /></span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <div className="card-cta-row">
                <a
                  href={MGRANT_URL}
                  className="btn btn-primary-maroon"
                  data-testid="donors-visit-btn"
                >
                  Open mGrant <ArrowRight size={16} />
                </a>
                <button
                  type="button"
                  onClick={() => openBook("mgrant", "maroon")}
                  className="btn btn-ghost"
                  data-testid="donors-book-btn"
                >
                  <Mail size={15} /> Book a session
                </button>
              </div>

              <div className="card-foot">
                <ArrowRight size={12} /> Continues on mgrant.in/mgrant
              </div>
            </article>

            {/* ———— NGO CARD ———— */}
            <article className="product-card card-ngos" data-testid="product-card-ngos">
              <div className="card-head">
                <div className="card-logo-img-wrap">
                  <img src={MGRANT_LOGO} alt="mGrant NUO" className="card-logo-img" />
                  <span className="nuo-tag">NUO</span>
                </div>
                <span className="card-badge" data-testid="ngos-card-badge">
                  <Sparkles size={12} /> For NGOs & Non-Profits
                </span>
              </div>

              <h3 className="card-title sr-only" data-testid="ngos-card-title">mGrant NUO</h3>
              <div className="card-tagline" data-testid="ngos-card-tagline">
                Grant management for Indian Non-Profit Organisations.
              </div>
              <p className="card-desc" data-testid="ngos-card-desc">
                A purpose-built platform for NGOs — fundraising CRM, grant lifecycle, budget
                tracking, LFA-based impact, beneficiary registry, and FCRA compliance, all in
                one workspace.
              </p>

              <div className="card-stats" data-testid="ngos-card-stats">
                {ngoStats.map((s, i) => (
                  <div key={i}>
                    <div className="stat-num">{s.num}</div>
                    <div className="stat-lbl">{s.lbl}</div>
                  </div>
                ))}
              </div>

              <ul className="card-feats" data-testid="ngos-card-feats">
                {ngoFeats.map((f, i) => (
                  <li className="card-feat" key={i}>
                    <span className="feat-tick"><Check size={11} strokeWidth={3} /></span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <div className="card-cta-row">
                <a
                  href={MGRANT_NUO_URL}
                  className="btn btn-primary-orange"
                  data-testid="ngos-visit-btn"
                >
                  Open mGrant NUO <ArrowRight size={16} />
                </a>
                <button
                  type="button"
                  onClick={() => openBook("mgrant_nuo", "orange")}
                  className="btn btn-ghost"
                  data-testid="ngos-book-btn"
                >
                  <Mail size={15} /> Book a session
                </button>
              </div>

              <div className="card-foot">
                <ArrowRight size={12} /> Continues on mgrant.in/mgrant-nuo
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* ———— ABOUT / TRUST STRIP ———— */}
      <section id="about" className="about" data-testid="about-section">
        <div className="about-inner">
          <div>
            <img src={DHWANI_LOGO} alt="Dhwani Rural Information Systems" className="dhwani-logo" data-testid="about-dhwani-logo" />
            <h3 data-testid="about-heading">
              One platform family. <em>Two focused products.</em>
            </h3>
            <p data-testid="about-desc">
              mGrant is a <strong>Dhwani Rural Information Systems</strong> product.
              Both platforms are hosted on AWS Mumbai, aligned with the DPDP Act 2023, and
              built to match how Indian CSR teams and Non-Profits actually work — no
              spreadsheets, no shortcuts, full audit trail.
            </p>
            <div className="about-ctas">
              <button
                type="button"
                onClick={() => openBook("landing", "orange")}
                className="btn btn-primary-orange"
                data-testid="about-email-btn"
              >
                <Mail size={15} /> Book a session
              </button>
              <a
                href="https://www.dhwaniris.com/"
                target="_blank" rel="noopener noreferrer"
                className="btn btn-dark"
                data-testid="about-dhwani-btn"
              >
                About Dhwani RIS <ExternalLink size={14} />
              </a>
            </div>
          </div>

          <div className="about-certs" data-testid="about-certs">
            <div className="cert">
              <div className="cert-label">Certified</div>
              <div className="cert-name">ISO 27001:2013</div>
            </div>
            <div className="cert">
              <div className="cert-label">Cleared</div>
              <div className="cert-name">VAPT — 3rd party</div>
            </div>
            <div className="cert">
              <div className="cert-label">Aligned</div>
              <div className="cert-name">DPDP Act 2023</div>
            </div>
            <div className="cert">
              <div className="cert-label">Hosted</div>
              <div className="cert-name">AWS Mumbai · Multi-AZ</div>
            </div>
          </div>
        </div>
      </section>

      {/* ———— FOOTER ———— */}
      <footer className="footer" data-testid="site-footer">
        <div className="footer-inner">
          <div className="footer-brand">
            <img src={MGRANT_LOGO} alt="mGrant" className="footer-logo" />
            <span>mgrant.in · A Dhwani RIS product</span>
          </div>
          <div className="footer-links">
            <a href={MGRANT_URL} className="footer-link" data-testid="footer-mgrant-link">
              mGrant (Donors)
            </a>
            <a href={MGRANT_NUO_URL} className="footer-link" data-testid="footer-mgrant-nuo-link">
              mGrant NUO (NGOs)
            </a>
            <a href={`mailto:${EMAIL}`} className="footer-link" data-testid="footer-email-link">
              {EMAIL}
            </a>
            <a href="https://www.dhwaniris.com/" target="_blank" rel="noopener noreferrer" className="footer-link" data-testid="footer-dhwani-link">
              dhwaniris.com
            </a>
          </div>
          <div className="footer-copy">© {new Date().getFullYear()} Dhwani RIS</div>
        </div>
      </footer>

      <BookSessionModal
        open={modal.open}
        onClose={() => setModal((m) => ({ ...m, open: false }))}
        source={modal.source}
        accent={modal.accent}
      />
    </>
  );
}
